import { useState, useEffect } from 'react';
import { useSupabaseAuth } from '@/hooks/auth';
import { supabase } from '@/integrations/supabase/client';
import { 
  fetchUsers, 
  fetchApprovalRequests, 
  updateUserRole,
  approveUser,
  rejectUser,
  toggleApproval,
  deleteUser
} from '@/pages/admin/userManagementService';
import { createUserByAdmin, resetUserPassword } from '@/pages/admin/adminUserService';
import { UserWithRole, ApprovalRequest, UserRole } from '@/pages/admin/types';
import { toast } from '@/components/ui/sonner';

export const useUserManagement = () => {
  const { session } = useSupabaseAuth();
  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [approvalRequests, setApprovalRequests] = useState<ApprovalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersData, requestsData] = await Promise.all([
        fetchUsers(),
        fetchApprovalRequests()
      ]);
      setUsers(usersData);
      setApprovalRequests(requestsData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleBulkApprove = async (userIds: string[]): Promise<boolean> => {
    try {
      let successCount = 0;
      const results = await Promise.all(
        userIds.map(async (userId) => {
          const result = await approveUser(userId, session?.user?.id || '');
          return result;
        })
      );
      
      successCount = results.filter(result => result === true).length;
      if (successCount > 0) {
        await loadData();
        toast.success(`Successfully approved ${successCount} users`);
        return true;
      } else {
        toast.error('Failed to approve users');
        return false;
      }
    } catch (error) {
      console.error('Error in bulk approve:', error);
      toast.error('Failed to approve users');
      return false;
    }
  };

  const handleBulkRoleChange = async (userIds: string[], role: string): Promise<boolean> => {
    try {
      let successCount = 0;
      const results = await Promise.all(
        userIds.map(async (userId) => {
          const result = await updateUserRole(userId, role as UserRole);
          return result;
        })
      );
      
      successCount = results.filter(result => result === true).length;
      if (successCount > 0) {
        await loadData();
        toast.success(`Successfully updated ${successCount} user roles`);
        return true;
      } else {
        toast.error('Failed to update user roles');
        return false;
      }
    } catch (error) {
      console.error('Error in bulk role change:', error);
      toast.error('Failed to update user roles');
      return false;
    }
  };

  const handleUserInvite = async (userData: { email: string; name: string; role: UserRole }) => {
    try {
      const result = await createUserByAdmin({
        email: userData.email,
        name: userData.name,
        role: userData.role
      });
      if (result) {
        toast.success('User created successfully');
        await loadData();
        return true;
      } else {
        toast.error('Failed to create user');
        return false;
      }
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Failed to create user');
      return false;
    }
  };

  const handlePasswordReset = async (userData: { userId: string; newPassword: string }) => {
    try {
      const result = await resetUserPassword({
        userId: userData.userId,
        newPassword: userData.newPassword
      });
      if (result) {
        toast.success('Password reset successfully');
        return true;
      } else {
        toast.error('Failed to reset password');
        return false;
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Failed to reset password');
      return false;
    }
  };

  const handleRoleUpdate = async (userId: string, newRole: string) => {
    try {
      const result = await updateUserRole(userId, newRole as UserRole);
      if (result) {
        toast.success('User role updated successfully');
        await loadData();
        return true;
      } else {
        toast.error('Failed to update user role');
        return false;
      }
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Failed to update user role');
      return false;
    }
  };

  const handleApprovalToggle = async (userId: string, currentStatus: boolean) => {
    try {
      const result = await toggleApproval(userId, !currentStatus);
      if (result) {
        toast.success(`User ${!currentStatus ? 'approved' : 'unapproved'} successfully`);
        await loadData();
        return true;
      } else {
        toast.error('Failed to update approval status');
        return false;
      }
    } catch (error) {
      console.error('Error toggling approval:', error);
      toast.error('Failed to update approval status');
      return false;
    }
  };

  const handleApprovalRequestApprove = async (userId: string) => {
    const result = await approveUser(userId, session?.user?.id || '');
    if (result) {
      toast.success('User approved successfully');
      await loadData();
    } else {
      toast.error('Failed to approve user');
    }
  };

  const handleApprovalRequestReject = async (userId: string) => {
    const result = await rejectUser(userId, session?.user?.id || '');
    if (result) {
      toast.success('User request rejected');
      await loadData();
    } else {
      toast.error('Failed to reject user');
    }
  };

  const handleUserDelete = async (userId: string, force: boolean = false): Promise<boolean> => {
    try {
      // Use the new complete deletion edge function
      const { data, error } = await supabase.functions.invoke('admin-delete-user-complete', {
        body: { userId, force }
      }) as any;

      if (error) throw error;

      if (data?.success) {
        toast.success(data.message);
        await loadData();
        return true;
      } else {
        toast.error(data?.error || 'Failed to delete user');
        return false;
      }
    } catch (error: any) {
      console.error('Error deleting user:', error);
      toast.error(error.message || 'Failed to delete user');
      return false;
    }
  };

  return {
    users,
    approvalRequests,
    loading,
    selectedUsers,
    setSelectedUsers,
    loadData,
    handleBulkApprove,
    handleBulkRoleChange,
    handleUserInvite,
    handlePasswordReset,
    handleRoleUpdate,
    handleApprovalToggle,
    handleApprovalRequestApprove,
    handleApprovalRequestReject,
    handleUserDelete
  };
};
